<template>
  <v-dialog
    v-model="isDialogOpen"
    max-width="980px"
    content-class="fixed-height-lg"
  >
    <modal-card
      v-if="isDialogOpen"
      title="Invite Contacts"
      fixed
      @close="isDialogOpen = false"
    >
      <div v-if="showSuccess" class="invite-confirmation-overlay">
        <div class="invite-confirmation-content">
          <v-icon size="64" color="success" class="invite-success-icon">
            mdi-check-circle
          </v-icon>
          <h3 class="invite-confirmation-title">
            Invitations Sent Successfully!
          </h3>
          <p class="invite-confirmation-message">
            {{ invitedCount }} contacts have been invited to join your platform.
          </p>
          <div class="invite-confirmation-actions">
            <v-btn color="primary" outlined @click="importMore">
              <v-icon left>mdi-plus</v-icon>
              Invite More
            </v-btn>
            <v-btn color="appMain" @click="finishInviting">
              <v-icon left>mdi-check</v-icon>
              Finish
            </v-btn>
          </div>
        </div>
      </div>

      <contacts-table
        ref="contactsTable"
        class="invite-contacts-table"
        :class="{ 'table-blurred': showSuccess }"
        shortHeader
        showSelectedButton
        hideControls
        insideModal
        ignoreMargin
        :show-select="true"
        :disableSelectionForContacts="invitedContacts"
        v-model="selectedContacts"
      />

      <template #actions>
        <v-btn
          color="primary"
          :loading="isInviting"
          :disabled="selectedContacts.length === 0"
          @click="inviteSelectedContacts"
        >
          Invite ({{ selectedContacts.length }})
        </v-btn>
      </template>
    </modal-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ContactsTable from '@/views/contacts/components/ContactsTable.vue';

export default {
  name: 'InviteContactsDialog',
  components: {
    ContactsTable,
  },
  props: {
    value: Boolean,
  },
  data() {
    return {
      selectedContacts: [],
      isInviting: false,
      showSuccess: false,
      invitedCount: 0,
    };
  },
  computed: {
    ...mapGetters(['invitedContacts']),
    isDialogOpen: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  watch: {
    isDialogOpen(newVal) {
      if (!newVal) {
        this.resetState();
      }
    },
  },
  methods: {
    ...mapActions(['inviteContacts']),

    async inviteSelectedContacts() {
      if (this.selectedContacts.length === 0) return;

      try {
        this.isInviting = true;
        const contactIds = this.selectedContacts.map(
          (contact) => contact.con_id
        );
        await this.inviteContacts({ contact_ids: contactIds });

        // Show success state
        this.invitedCount = this.selectedContacts.length;
        this.showSuccess = true;
        this.selectedContacts = [];

        this.$emit('checkAchievement');
      } catch (error) {
        console.error('Error inviting contacts:', error);
      } finally {
        this.isInviting = false;
      }
    },

    importMore() {
      this.showSuccess = false;
      this.selectedContacts = [];
    },

    finishInviting() {
      this.$emit('finish');
    },

    resetState() {
      this.selectedContacts = [];
      this.isInviting = false;
      this.showSuccess = false;
      this.invitedCount = 0;
    },
  },
};
</script>

<style lang="less">
// Invite Confirmation Overlay
.invite-confirmation-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;

  .invite-confirmation-content {
    text-align: center;
    max-width: 400px;
    background: white;
    border-radius: 8px;
    padding: 32px 24px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);

    .invite-success-icon {
      margin-bottom: 24px;
      animation: inviteSuccessPulse 0.6s ease-out;
    }

    .invite-confirmation-title {
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--v-success-base);
      margin-bottom: 16px;
    }

    .invite-confirmation-message {
      font-size: 1rem;
      color: var(--v-inactive-base);
      margin-bottom: 32px;
      line-height: 1.5;
    }

    .invite-confirmation-actions {
      display: flex;
      gap: 12px;
      justify-content: center;
      flex-wrap: wrap;

      .v-btn {
        min-width: 120px;
      }
    }
  }
}

.invite-contacts-table {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  &.invite-table-blurred {
    filter: blur(2px);
    opacity: 0.6;
    pointer-events: none;
  }
}

@keyframes inviteSuccessPulse {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
